import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { isCalendarEventWithinRange } from '../helpers';
import { useSchedulerV3QueryUpdaters } from '../query-updaters';

dayjs.extend(utc);
dayjs.extend(isBetween);

export const useSchedulerV3InvalidateQueries = () => {
  const { invalidateQueries } = useSchedulerV3QueryUpdaters();

  const invalidateGetAppointmentType = (appointmentTypeId: string) =>
    invalidateQueries({
      endpointName: 'GetAppointmentType',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return request.appointmentTypeId === appointmentTypeId;
        },
      },
    });

  const invalidateListAppointmentTypes = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListAppointmentTypes',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListAppointmentTypesMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationsIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidateListProviders = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListProviders',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListProvidersMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidGetProvider = (providerId: string) =>
    invalidateQueries({
      endpointName: 'GetProvider',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => queryKey[2].id === providerId,
      },
    });

  const invalidateListMappings = (locationId?: string) =>
    invalidateQueries({
      endpointName: 'ListMappings',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationIds ?? []).includes(locationId);
        },
      },
    });

  // entityIds is an array of locationIds or providerIds
  const invalidateListSchedules = (entityIds: string[]) =>
    invalidateQueries({
      endpointName: 'ListSchedules',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !!request.ids?.some((entityId) => entityIds?.includes(entityId));
        },
      },
    });

  const invalidateListScheduleActivity = ({
    locationId,
    startDateTime,
    endDateTime,
    oldStartDateTime,
    oldEndDateTime,
  }: {
    locationId?: string;
    startDateTime?: string;
    endDateTime?: string;
    oldStartDateTime?: string;
    oldEndDateTime?: string;
  }) => {
    // Skip invalidation if the appointment datetime is not changed
    if (startDateTime && endDateTime && startDateTime === endDateTime && oldStartDateTime === oldEndDateTime) {
      return;
    }

    invalidateQueries({
      endpointName: 'ListScheduleActivity',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => !locationId || queryKey[2].locationId === locationId,
      },
    });
  };

  const invalidateListScheduleActivityHistory = ({
    locationId,
    startDateTime,
    endDateTime,
    oldStartDateTime,
    oldEndDateTime,
    referenceId,
  }: {
    locationId?: string;
    startDateTime?: string;
    endDateTime?: string;
    oldStartDateTime?: string;
    oldEndDateTime?: string;
    referenceId?: string;
  }) => {
    // Skip invalidation if the appointment datetime is not changed
    if (startDateTime && endDateTime && startDateTime === endDateTime && oldStartDateTime === oldEndDateTime) {
      return;
    }

    invalidateQueries({
      endpointName: 'ListScheduleActivityHistory',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          if (referenceId) {
            return request.appointmentId === referenceId;
          }
          return !locationId || request.locationId === locationId;
        },
      },
    });
  };

  const invalidateListBookingSubmissions = (locationId?: string) =>
    invalidateQueries({
      endpointName: 'ListBookingSubmissions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationsIds ?? []).includes(locationId);
        },
      },
    });

  const invalidateGetBookingSubmission = (bookingSubmissionId?: string) =>
    invalidateQueries({
      endpointName: 'GetBookingSubmission',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => !!bookingSubmissionId || queryKey[2].submissionId === bookingSubmissionId,
      },
    });

  const invalidateListCalendarEventsByAttendee = (locationId?: string) => {
    // TODO: Update cache instead of invalidating all queries
    invalidateQueries({
      endpointName: 'ListCalendarEventsByAttendee',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          if (!locationId) return true;

          const request = queryKey[2];
          return request.locationId === locationId || (request.childLocationsIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidateListCalendarEvents = ({
    locationId,
    startDateTime,
    endDateTime,
    oldStartDateTime,
    oldEndDateTime,
    ignoreDate = false,
  }: {
    locationId?: string;
    startDateTime?: string;
    endDateTime?: string;
    oldStartDateTime?: string;
    oldEndDateTime?: string;
    ignoreDate?: boolean;
  }) => {
    const matches = (request: { startDateTime?: string; endDateTime?: string }) => {
      if (ignoreDate) return true;

      const matchesNewDateRange = isCalendarEventWithinRange({
        startDate: startDateTime,
        endDate: endDateTime,
        rangeStart: request.startDateTime,
        rangeEnd: request.endDateTime,
      });

      const matchesOldDateRange = isCalendarEventWithinRange({
        startDate: oldStartDateTime,
        endDate: oldEndDateTime,
        rangeStart: request.startDateTime,
        rangeEnd: request.endDateTime,
      });

      return matchesNewDateRange || matchesOldDateRange;
    };

    invalidateQueries({
      endpointName: 'ListCalendarEvents',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => (!locationId || queryKey[2].locationId === locationId) && matches(queryKey[2]),
      },
    });

    invalidateQueries({
      endpointName: 'ListCalendarEventsMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return (!locationId || (request.locationsIds ?? []).includes(locationId)) && matches(request);
        },
      },
    });
  };

  const invalidateGetCalendarEvent = (eventId?: string) =>
    invalidateQueries({
      endpointName: 'GetCalendarEvent',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !eventId || request.eventId === eventId;
        },
      },
    });

  const invalidateCalendarEventsForApptType = (locationId?: string) => {
    // invalidate all calendar events for the location since appt type could be used anywhere
    invalidateListCalendarEvents({
      locationId,
      ignoreDate: true,
    });
    invalidateGetCalendarEvent();
    invalidateListCalendarEventsByAttendee(locationId);
  };

  const invalidateListOperatories = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListOperatories',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListOperatoriesMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationsIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidateGetOperatory = (operatoryId?: string) =>
    invalidateQueries({
      endpointName: 'GetOperatory',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !operatoryId || request.operatoryId === operatoryId;
        },
      },
    });

  const invalidateBookingSubmissions = (locationIds: string[]) => {
    invalidateQueries({
      endpointName: 'ListBookingSubmissions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const queryKeyLocationIds = queryKey[2].locationsIds || [];
          return queryKeyLocationIds.some((id) => locationIds.includes(id));
        },
      },
    });
  };

  const invalidateListLocationSettings = (locationId: string) =>
    invalidateQueries({
      endpointName: 'ListLocationSettings',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !!locationId && (request.locationIds ?? []).includes(locationId);
        },
      },
    });

  // Service Types
  const invalidateListGroupedBookableAppointmentTypes = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListGroupedBookableAppointmentTypes',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListBookableAppointmentTypeOptions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });
  };

  const invalidateListScheduleDefinitions = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListScheduleDefinitions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });
  };

  // Rule Exceptions
  const invalidateRuleExceptions = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListScheduleExceptions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });
  };

  return {
    invalidateListAppointmentTypes,
    invalidateGetAppointmentType,
    invalidateListProviders,
    invalidGetProvider,
    invalidateListMappings,
    invalidateListSchedules,
    invalidateListScheduleActivity,
    invalidateListScheduleActivityHistory,
    invalidateListBookingSubmissions,
    invalidateGetBookingSubmission,
    invalidateListCalendarEventsByAttendee,
    invalidateListCalendarEvents,
    invalidateGetCalendarEvent,
    invalidateCalendarEventsForApptType,
    invalidateListOperatories,
    invalidateGetOperatory,
    invalidateBookingSubmissions,
    invalidateListLocationSettings,
    invalidateListGroupedBookableAppointmentTypes,
    invalidateListScheduleDefinitions,
    invalidateRuleExceptions,
  };
};
